/**
 * narrativeSanitiser.js
 *
 * RC 1.46.NARRATIVE-INTEGRITY (2026-05-02).
 *
 * Strips numeric sub-score references from a score_explanation
 * narrative so the prose is consistent with the Path A/Path B
 * dual-grader architecture.
 *
 * Why
 * ---
 * `analyze_job_fit` (Path A, holistic) emits `score_explanation` as
 * part of its JSON output. Path A's narrative was built using its
 * OWN internal CV self-grade, which is generous. RC 1.46.DUAL-GRADER
 * then overwrites the displayed `cv_sub_score` with Path B's strict
 * value (e.g. 41) — but the narrative text is not regenerated, so
 * it can claim things like "the lowest sub-score is seniority at
 * 46/100" while the user sees the chip showing CV verdict at 41.
 *
 * Operator decision (2026-05-02): strip percentages, "/100" values,
 * and numeric sub-score references from the rendered narrative.
 * Qualitative facts only. The numbers are surfaced separately via
 * the chips and breakdown rows.
 *
 * The prompt rule shipped in the same RC instructs new analyses to
 * avoid these references at source — but legacy `score_explanation`
 * rows in the DB still carry numbers, so this client-side
 * sanitiser is the back-compat shield.
 *
 * Design notes
 * ------------
 *   * Idempotent: running it twice yields the same output.
 *   * Length-bounded: strip operations only remove, never insert,
 *     so output ≤ input.
 *   * Whitespace-clean: collapses multi-space, multi-comma, and
 *     trailing-punctuation artefacts left behind after numeric
 *     fragments are removed.
 *   * Returns the original string unchanged when it doesn't contain
 *     any numeric patterns — fast path for prompt-rule-compliant
 *     analyses.
 */

// Patterns we strip. Order matters — longer / more-specific first.
// `\b` after `%` would require a word boundary between `%` (non-word)
// and a following `)` (also non-word), so it never matched
// parenthesised cases like `(72%)`. Drop it; the leading `\b` plus
// `\d{1,3}` is sufficient to anchor the start.
const PERCENTAGE_RE   = /\s*\(?\b\d{1,3}\s*%\)?/g;              // 41%, (55%), 100%
const SLASH_100_RE    = /\s*\(?\b\d{1,3}\s*\/\s*100\b\)?/g;     // 41/100, (55/100)
const SCORING_AT_RE   = /\bscoring\s+(?:a\s+|an\s+)?\d{1,3}\b/gi; // "scoring a 41"
const SUB_SCORE_AT_RE = /\b(salary|seniority|location|strategic|risk|cv\s+sub[-\s]?score|cv\s+verdict|overall\s+fit|fit\s+score)(\s+(?:score|sub[-\s]?score|fit))?\s+(?:at|of|=)\s+\d{1,3}\b/gi;

// Tidy-up phase (post-strip).
const MULTI_SPACE_RE   = / {2,}/g;
const SPACE_BEFORE_PUNCT_RE = / +([.,;:?!])/g;
const DUP_COMMA_RE     = /,\s*,/g;
const DUP_PERIOD_RE    = /\.\s*\./g;
const ORPHAN_AT_END_RE = /\s+(?:at|of|=|with)\s*[.;,!?]?\s*$/i;

/**
 * Strip numeric sub-score references from a narrative string.
 * Safe on null / undefined / empty input.
 *
 * @param {string|null|undefined} text  raw score_explanation text
 * @returns {string}                     sanitised narrative
 */
export function sanitiseNarrative(text) {
  if (!text || typeof text !== 'string') return '';

  let s = text;

  // Strip numeric fragments. Keep going until the regex doesn't
  // match anymore (handles overlapping patterns).
  s = s.replace(SUB_SCORE_AT_RE, (match) => {
    // Keep the label, drop the "at 46" tail.
    return match.replace(/\s+(?:at|of|=)\s+\d{1,3}\s*$/i, '');
  });
  s = s.replace(SCORING_AT_RE, 'scoring');
  s = s.replace(SLASH_100_RE, '');
  s = s.replace(PERCENTAGE_RE, '');

  // Tidy whitespace and punctuation artefacts.
  s = s.replace(MULTI_SPACE_RE, ' ');
  s = s.replace(SPACE_BEFORE_PUNCT_RE, '$1');
  s = s.replace(DUP_COMMA_RE, ',');
  s = s.replace(DUP_PERIOD_RE, '.');
  s = s.replace(ORPHAN_AT_END_RE, '.');
  s = s.trim();

  return s;
}

export default sanitiseNarrative;
